import { REGEX } from '@/constants/regex';
import {
  DarkPrimaryBackground,
  LightPrimaryBackground,
  DarkAppBarBackground,
  LightAppBarBackground,
  DarkHeroBackground,
  LightHeroBackground,
  EmptyAvatar,
} from '@/images/brandingThemeDemos';
import { BackIcon } from '@/images/brandingThemeDemos/icons/back';
import { DepositLottie } from '@/images/brandingThemeDemos/icons/DepositLottie';
import { HomeLottie } from '@/images/brandingThemeDemos/icons/HomeLottie';
import { MenuIcon } from '@/images/brandingThemeDemos/icons/menu';
import { PaySendLottie } from '@/images/brandingThemeDemos/icons/PaySendLottie';
import { PrivateBankerLottie } from '@/images/brandingThemeDemos/icons/PrivateBankerLottie';
import {
  BrandingThemeCustomBackgroundOptions,
  BrandingThemeFonts,
} from '@/types/brandingThemes';

import { generateColorShades, invertColor } from '@/utils/color';
import { Box, Grid, Avatar, Typography } from '@mui/material';

type MobileUIDemoProps = {
  navigationBackground: BrandingThemeCustomBackgroundOptions;
  heroBackground: BrandingThemeCustomBackgroundOptions;
  homeScreenBackground: BrandingThemeCustomBackgroundOptions;
  primaryColor: string;
  secondaryColor: string;
  bottomNavIconSelectedColor: string;
  titleDefaultColor: string;
  heroSectionColor: string;
  titleAccentSectionColor: string;
  font: BrandingThemeFonts;
  logoImage: string;
};

export default function MobileUIDemo({
  navigationBackground,
  heroBackground,
  homeScreenBackground,
  primaryColor,
  secondaryColor,
  bottomNavIconSelectedColor,
  titleDefaultColor,
  heroSectionColor,
  titleAccentSectionColor,
  font,
  logoImage,
}: MobileUIDemoProps) {
  const isValidColor = (color: string) =>
    new RegExp(REGEX.HEX_COLOR).test(color);
  const iconColorsAreReady =
    isValidColor(primaryColor) && isValidColor(bottomNavIconSelectedColor);

  const styles = {
    container: {
      margin: '0 auto',
      marginBottom: '20px',
      backgroundImage:
        homeScreenBackground === 'Dark Metal'
          ? `url("${DarkPrimaryBackground}")`
          : `url("${LightPrimaryBackground}")`,
      maxWidth: '390px',
      width: '100%',
      height: '770px',
    },
    header: {
      height: '75px',
      backgroundImage:
        navigationBackground === 'Dark Metal'
          ? `url("${DarkAppBarBackground}")`
          : `url("${LightAppBarBackground}")`,
      borderBottom: `3px solid ${primaryColor}`,
      alignItems: 'center',
      justifyContent: logoImage ? 'space-between' : 'flex-end',
      padding: '0 0 0 15px',
    },
    logoImage: { maxHeight: '40px', maxWidth: '100%' },
    hero: {
      height: '240px',
      backgroundImage:
        heroBackground === 'Dark Metal'
          ? `url("${DarkHeroBackground}")`
          : `url("${LightHeroBackground}")`,
      position: 'relative',
    },
    backButton: { position: 'absolute', left: '15px', top: '15px' },
    privateBankerWrapper: {
      position: 'absolute',
      right: '0',
      top: '10px',
      backgroundColor: '#fff',
      borderTopLeftRadius: '45%',
      borderBottomLeftRadius: '45%',
      padding: '5px 20px 5px 5px',
    },
    avatar: {
      width: 50,
      height: 50,
      borderRadius: '50%',
      border: '3px solid green',
    },
    smallHeroText: {
      fontFamily: font,
      fontSize: '30px',
      padding: '0 15px',
      paddingTop: '110px',
      color: heroSectionColor,
    },
    largeHeroText: {
      fontFamily: font,
      fontSize: '45px',
      padding: '0 15px',
      color: heroSectionColor,
    },
    content: { height: '380px', padding: '0 15px' },
    titleText: {
      fontFamily: font,
      fontSize: '30px',
      margin: '10px 0',
      color: titleDefaultColor,
    },
    accentText: {
      fontFamily: font,
      fontSize: '20px',
      color: titleAccentSectionColor,
    },
    input: {
      margin: '5px 0 10px 0',
      border: '1px solid #999',
      borderRadius: 1,
      height: '50px',
      backgroundColor: '#fff',
    },
    button: {
      borderRadius: 1,
      height: '40px',
      textAlign: 'center',
      lineHeight: '40px',
      fontFamily: font,
    },
    primaryButton: {
      margin: '25px 0 10px',
      backgroundColor: primaryColor,
      color: invertColor(primaryColor),
      '&:hover': {
        backgroundColor: generateColorShades(primaryColor, 100),
      },
    },
    secondaryButton: {
      margin: '10px 0',
      backgroundColor: secondaryColor,
      color: invertColor(secondaryColor),
      '&:hover': {
        backgroundColor: generateColorShades(secondaryColor, 100),
      },
    },
    bottomNav: {
      height: '75px',
      backgroundImage:
        navigationBackground === 'Dark Metal'
          ? `url("${DarkAppBarBackground}")`
          : `url("${LightAppBarBackground}")`,
      borderTop: `3px solid ${primaryColor}`,
      alignItems: 'center',
    },
    bottomNavItem: {
      fontFamily: font,
      fontSize: '14px',
      color: primaryColor,
      textAlign: 'center',
    },
    bottomNavItemSelected: {
      fontFamily: font,
      fontSize: '14px',
      color: bottomNavIconSelectedColor,
      textAlign: 'center',
    },
  };

  return (
    <Box sx={styles.container}>
      <Grid container sx={styles.header}>
        <Grid size={{ xs: 8 }}>
          {logoImage && (
            <img style={styles.logoImage} alt={'Mobile Logo'} src={logoImage} />
          )}
        </Grid>
        <Grid size={{ xs: 2 }}>
          <MenuIcon colorFill={primaryColor} />
        </Grid>
      </Grid>
      <Box sx={styles.hero}>
        <Box sx={styles.backButton}>
          <BackIcon colorFill={primaryColor} />
        </Box>
        <Box sx={styles.privateBankerWrapper}>
          <Avatar src={EmptyAvatar} sx={styles.avatar} />
        </Box>
        <Typography sx={styles.smallHeroText}>TITLE HERO</Typography>
        <Typography sx={styles.largeHeroText}>TITLE HERO</Typography>
      </Box>
      <Box sx={styles.content}>
        <Typography sx={styles.titleText}>TITLE</Typography>
        <Typography sx={styles.accentText}>TITLE ACCENT</Typography>
        <Box sx={styles.input}></Box>
        <Typography sx={styles.accentText}>TITLE ACCENT</Typography>
        <Box sx={styles.input}></Box>
        <Box sx={{ ...styles.button, ...styles.primaryButton }}>SUBMIT</Box>
        <Box sx={{ ...styles.button, ...styles.secondaryButton }}>CANCEL</Box>
      </Box>
      <Grid container sx={styles.bottomNav}>
        {iconColorsAreReady && (
          <>
            <Grid size={{ xs: 3 }}>
              <HomeLottie color={primaryColor} />
              <Typography sx={styles.bottomNavItem}>Home</Typography>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <PaySendLottie color={bottomNavIconSelectedColor} />
              <Typography sx={styles.bottomNavItemSelected}>
                Pay & Send
              </Typography>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <DepositLottie color={primaryColor} />
              <Typography sx={styles.bottomNavItem}>Deposit</Typography>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <PrivateBankerLottie color={primaryColor} />
              <Typography sx={styles.bottomNavItem}>Client Concierge</Typography>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
}
